import React from 'react';
import cn from 'classnames';
import {ComponentSize, StandardProps} from '..';
import FormControlContext, {FormControlContextValue} from './FormControlContext';

interface FormControlRootProps extends StandardProps<React.HTMLAttributes<HTMLDivElement>> {
  as?: React.ElementType;
  size?: ComponentSize;
  disabled?: boolean;
}

export const FormControlRoot = React.forwardRef<HTMLDivElement, FormControlRootProps>((props, ref) => {
  const {
    as: Component = 'div',
  } = props;

  return (
    <Component
      ref={ref}
      className={cn('form-control form-control__root', props.className, {
        'size-small': props.size === 'small',
        'form-control--disabled': props.disabled,
      })}
      style={props.style}
    >
      {props.children}
    </Component>
  );
});

export interface FormControlProps extends StandardProps<React.HTMLAttributes<HTMLDivElement>> {
  component?: React.ElementType;
  variant?: 'filled' | 'outlined' | 'standard';
  size?: ComponentSize;
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
  fullWidth?: boolean;
  required?: boolean;
}

export const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>((props, ref) => {
  const {
    children,
    component = 'div',
    disabled = false,
    error = false,
    fullWidth = false,
    focused: visuallyFocused,
    required = false,
    variant = 'outlined',
    size = 'medium',
    ...other
  } = props;

  const [focusedState, setFocused] = React.useState(false);
  if (disabled && focusedState) {
    setFocused(false);
  }
  const focused = visuallyFocused !== undefined && !disabled ? visuallyFocused : focusedState;

  const childContext: FormControlContextValue = {
    variant,
    size,
    disabled,
    error,
    focused,
    fullWidth,
    required,
    onBlur: () => {
      setFocused(false);
    },
    onFocus: () => {
      setFocused(true);
    },
  };

  return (
    <FormControlContext.Provider value={childContext}>
      <FormControlRoot
        as={component}
        className={props.className}
        size={size}
        ref={ref}
        disabled={disabled}
        {...other}
      >
        {children}
      </FormControlRoot>
    </FormControlContext.Provider>
  );
});

export default FormControl;
